import { AxnAdminUserPermissions } from "@idw-types/data/axn-admin-user";
import { NavigationMenuItem } from "@idw-types/common";
import { faHome } from "@fortawesome/free-solid-svg-icons/faHome";
import { faShieldVirus } from "@fortawesome/free-solid-svg-icons/faShieldVirus";
import { faList } from "@fortawesome/free-solid-svg-icons/faList";
import { faCodeBranch } from "@fortawesome/free-solid-svg-icons/faCodeBranch";
import { faArrowsAlt } from "@fortawesome/free-solid-svg-icons/faArrowsAlt";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons/faExclamationTriangle";
import { faClipboard } from "@fortawesome/free-solid-svg-icons/faClipboard";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { faCog } from "@fortawesome/free-solid-svg-icons/faCog";
import { faChartBar } from "@fortawesome/free-solid-svg-icons/faChartBar";
import { faCopyright } from "@fortawesome/free-solid-svg-icons/faCopyright";
import { faFileText } from "@fortawesome/free-solid-svg-icons/faFileText";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons/faSignOutAlt";
import {
    DASHBOARD_URL,
    WORKFLOWS_URL,
    VERIFICATION_SERVICES_URL,
    IDENTITY_PROVIDERS_URL,
    ORGANIZATION_ACTIVITY_URL,
    ORGANIZATIONS_URL,
    ORGANIZATION_USERS_URL,
    ORGANIZATION_INVITATIONS_URL,
    RELYING_PARTIES_URL,
    ALERT_EVENTS_URL,
    REPORTS_TRANSACTIONS_URL,
    REPORTS_SERVICE_ASSERTION_PERFORMANCE_URL,
    DISCLOSURES_URL,
    HELP_URL,
    MANAGEMENT_CONSOLE_AXN_CACHE_URL,
    MANAGEMENT_CONSOLE_AXN_CONFIG_URL,
    MANAGEMENT_CONSOLE_AXN_SESSIONS_URL,
    REPORTS_SERVICE_ASSERTION_PERFORMANCE_COMPARISON_URL,
    REPORTS_WORKFLOW_REPORT_URL,
    REPORTS_WORKFLOW_USAGE_DETAILS_URL,
    REPORTS_FRAUD_REPORT_URL,
    REPORTS_USER_SUMMARY_URL,
    REPORTS_ADAPTIVE_AUTHENTICATION_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_CACHE_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_CONFIG_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_LOGGING_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_URL,
    MANAGEMENT_CONSOLE_AXN_ADMIN_SESSIONS_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_URL,
    LOGOUT_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL,
    POLICY_COMPONENTS_URL,
    MESSAGING_TEMPLATES_URL,
    MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL,
    EMAIL_TEMPLATES_URL
} from "@constants/routes";
import { resources } from "@constants/hardcoded-strings";

export const getNavigationItemsForPermissions = (userPermissions: AxnAdminUserPermissions): Array<NavigationMenuItem> => {
    const navigationItems: Array<NavigationMenuItem> = [];

    //dashboard
    navigationItems.push({
        title: resources.navigationMenuItems.dashboard.title,
        idSuffix: "dashboard",
        icon: faHome,
        path: DASHBOARD_URL
    });

    //workflows
    if (userPermissions?.canListWorkflows) {
        navigationItems.push({
            title: resources.navigationMenuItems.workflows.title,
            idSuffix: "workflows",
            icon: faList,
            path: WORKFLOWS_URL
        });
    }

    //verification services
    if (userPermissions?.canListRPServices) {
        navigationItems.push({
            title: resources.navigationMenuItems.verificationServices.title,
            idSuffix: "verification-services",
            icon: faCodeBranch,
            path: VERIFICATION_SERVICES_URL
        });

    }

    //identity providers
    if (userPermissions?.canListIdentityProviders) {
        navigationItems.push({
            title: resources.navigationMenuItems.identityProviders.title,
            idSuffix: "identity-providers",
            icon: faArrowsAlt,
            path: IDENTITY_PROVIDERS_URL
        });
    }

    //organizations
    const organizationItem: NavigationMenuItem = {
        title: resources.navigationMenuItems.organization.title,
        idSuffix: "organization",
        icon: faUsers,
        children: []
    };

    // organization details
    if (userPermissions?.canListOrganizations) {
        organizationItem.children.push({
            title: resources.navigationMenuItems.organization.children.details.title,
            idSuffix: "organization-details",
            path: ORGANIZATIONS_URL
        });
    }

    // organization activity
    if (userPermissions?.canListOrganizationActivity) {
        organizationItem.children.push({
            title: resources.navigationMenuItems.organization.children.activity.title,
            idSuffix: "organization-activity",
            path: ORGANIZATION_ACTIVITY_URL
        });
    }

    if (userPermissions?.canListUsers) {
        // organization users
        organizationItem.children.push({
            title: resources.navigationMenuItems.organization.children.users.title,
            idSuffix: "organization-users",
            path: ORGANIZATION_USERS_URL
        });
    }

    if (userPermissions?.canListUserInvitations) {
        // organization invitations
        organizationItem.children.push({
            title: resources.navigationMenuItems.organization.children.invitations.title,
            idSuffix: "organization-invitations",
            path: ORGANIZATION_INVITATIONS_URL
        });
    }

    if (organizationItem.children.length > 0) {
        navigationItems.push(organizationItem);
    }

    // relying parties
    if (userPermissions?.canListRelyingParties) {
        navigationItems.push({
            title: resources.navigationMenuItems.relyingParties.title,
            idSuffix: "relying-parties",
            icon: faList,
            path: RELYING_PARTIES_URL
        });
    }

    // Policy Components
    if (userPermissions?.canListPolicyComponents) {
        navigationItems.push({
            title: resources.navigationMenuItems.policyComponents.title,
            idSuffix: "policy-components",
            icon: faShieldVirus,
            path: POLICY_COMPONENTS_URL
        });
    }

    if (userPermissions?.canListMessagingTemplates) {
        navigationItems.push({
            title: resources.navigationMenuItems.messagingTemplates.title,
            idSuffix: "messaging-templates",
            icon: faFileText,
            path: MESSAGING_TEMPLATES_URL
        });
    }

    if (userPermissions?.canListSelfServiceVerificationEmailTemplates) {
        navigationItems.push({
            title: resources.navigationMenuItems.emailTemplates.title,
            idSuffix: "email-templates",
            icon: faFileText,
            path: EMAIL_TEMPLATES_URL
        });
    }

    const alertsItem: NavigationMenuItem = {
        title: resources.navigationMenuItems.alerts.title,
        idSuffix: "alerts",
        icon: faExclamationTriangle,
        children: []
    };

    // alert events
    if (userPermissions?.canListAlertEvents) {
        alertsItem.children.push({
            title: resources.navigationMenuItems.alerts.children.alertEvents.title,
            idSuffix: "alert-events",
            path: ALERT_EVENTS_URL
        });
    }

    if (alertsItem.children.length > 0) {
        navigationItems.push(alertsItem);
    }

    // reports
    const reportsItem: NavigationMenuItem = {
        title: resources.navigationMenuItems.reports.title,
        idSuffix: "reports",
        icon: faChartBar,
        children: []
    };

    // report transactions
    if (userPermissions?.canViewTransactionReport) {
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.transactions.title,
            idSuffix: "transactions",
            path: REPORTS_TRANSACTIONS_URL
        });
    }

    // report service assertion performance
    if (userPermissions?.canViewServicePerformanceReport)
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.servicePerformance.title,
            idSuffix: "service-performance",
            path: REPORTS_SERVICE_ASSERTION_PERFORMANCE_URL
        });

    // report service assertion performance comparison
    if (userPermissions?.canViewServicePerformanceReport) {
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.serviceComparison.title,
            idSuffix: "service-performance-comparison",
            path: REPORTS_SERVICE_ASSERTION_PERFORMANCE_COMPARISON_URL
        });
    }

    // report workflow usage details
    if (userPermissions?.canViewWorkflowUsageReport) {
        // report workflow usage details
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.workflowUsageDetails.title,
            idSuffix: "workflow-usage-details",
            path: REPORTS_WORKFLOW_USAGE_DETAILS_URL
        });
    }

    if (userPermissions?.canViewWorkflowReport) {
        //report workflow report
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.workflowReport.title,
            idSuffix: "workflow-report",
            path: REPORTS_WORKFLOW_REPORT_URL
        });
    }

    if (userPermissions?.canViewFraudReport) {
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.fraudReport.title,
            idSuffix: "fraud-report",
            path: REPORTS_FRAUD_REPORT_URL
        });
    }

    if (userPermissions?.canViewUserSummaryReport) {
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.userSummary.title,
            idSuffix: "user-summary",
            path: REPORTS_USER_SUMMARY_URL
        });
    }

    if (userPermissions?.canViewAdaptiveAuthenticationReport) {
        reportsItem.children.push({
            title: resources.navigationMenuItems.reports.children.adaptiveAuthentication.title,
            idSuffix: "adaptive-authentication",
            path: REPORTS_ADAPTIVE_AUTHENTICATION_URL
        });
    }

    if (reportsItem.children.length > 0) {
        navigationItems.push(reportsItem);
    }

    // management console
    if (userPermissions?.canAccessManagementConsole) {
        navigationItems.push({
            title: resources.navigationMenuItems.managementConsole.title,
            idSuffix: "management-console",
            icon: faCog,
            children: [
                {
                    title: resources.navigationMenuItems.managementConsole.children.axn.title,
                    idSuffix: "axn",
                    children: [
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axn.children.cache.title,
                            idSuffix: "axn-cache",
                            path: MANAGEMENT_CONSOLE_AXN_CACHE_URL
                        },
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axn.children.config.title,
                            idSuffix: "axn-config",
                            path: MANAGEMENT_CONSOLE_AXN_CONFIG_URL
                        },
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axn.children.sessions.title,
                            idSuffix: "axn-sessions",
                            path: MANAGEMENT_CONSOLE_AXN_SESSIONS_URL
                        }
                    ]
                },
                {
                    title: resources.navigationMenuItems.managementConsole.children.axnAdmin.title,
                    idSuffix: "axnadmin",
                    children: [
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axnAdmin.children.cache.title,
                            idSuffix: "axnadmin-cache",
                            path: MANAGEMENT_CONSOLE_AXN_ADMIN_CACHE_URL
                        },
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axnAdmin.children.config.title,
                            idSuffix: "axnadmin-config",
                            path: MANAGEMENT_CONSOLE_AXN_ADMIN_CONFIG_URL
                        },
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axnAdmin.children.logging.title,
                            idSuffix: "axnadmin-logging",
                            path: MANAGEMENT_CONSOLE_AXN_ADMIN_LOGGING_URL
                        },
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axnAdmin.children.securityRules.title,
                            idSuffix: "axnadmin-security-rules",
                            path: MANAGEMENT_CONSOLE_AXN_ADMIN_SECURITY_RULES_URL
                        },
                        {
                            title: resources.navigationMenuItems.managementConsole.children.axnAdmin.children.sessions.title,
                            idSuffix: "axnadmin-sessions",
                            path: MANAGEMENT_CONSOLE_AXN_ADMIN_SESSIONS_URL
                        }
                    ]
                },
                {
                    title: resources.navigationMenuItems.managementConsole.children.masterData.title,
                    idSuffix: "master-data",
                    children: [
                        {
                            title: resources.navigationMenuItems.managementConsole.children.masterData.children.attributeLabels.title,
                            idSuffix: "attribute-labels",
                            path: MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_URL
                        },
                        {
                            title: resources.navigationMenuItems.managementConsole.children.masterData.children.workflowTemplates.title,
                            idSuffix: "workflow-templates",
                            path: MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_URL
                        }
                        ,
                        {
                            title: resources.navigationMenuItems.managementConsole.children.masterData.children.serviceTemplates.title,
                            idSuffix: "service-templates",
                            path: MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL
                        }
                    ]
                }
            ]
        });
    }

    // disclosures
    navigationItems.push({
        title: resources.navigationMenuItems.disclosures.title,
        idSuffix: "disclosures",
        icon: faClipboard,
        path: DISCLOSURES_URL
    })

    // help
    navigationItems.push({
        title: resources.navigationMenuItems.help.title,
        idSuffix: "help",
        icon: faQuestionCircle,
        path: HELP_URL
    });

    navigationItems.push({
        title: resources.navigationMenuItems.logout.title,
        idSuffix: "logout",
        icon: faSignOutAlt,
        path: LOGOUT_URL
    });

    // copyright
    navigationItems.push({
        title: resources.navigationMenuItems.copyright.title,
        idSuffix: "copyright",
        icon: faCopyright,
        path: "https://www.iddataweb.com",
        isExternalUrl: true,
    });

    return navigationItems;
};
